/**
 * Tiny fetch wrapper used by `base44Client.js`.
 *
 * - Base URL comes from `import.meta.env.VITE_API_URL`. In dev it defaults to
 *   `/api` so Vite's proxy (see vite.config.js) can forward to localhost:4000.
 * - The JWT is kept in localStorage under `tfin_token` and automatically
 *   attached as a Bearer token on every request.
 * - Responses are unwrapped to JSON. Non-2xx responses throw an Error with a
 *   `status` and `data` property so callers can branch on them.
 */
const TOKEN_KEY = 'tfin_token';
const DEFAULT_BASE = '/api';

function getBaseUrl() {
  // Vite replaces `import.meta.env.VITE_API_URL` at build time.
  const fromEnv =
    typeof import.meta !== 'undefined' && import.meta.env
      ? import.meta.env.VITE_API_URL
      : undefined;
  return (fromEnv && fromEnv.trim()) || DEFAULT_BASE;
}

function buildUrl(path, params) {
  const base = getBaseUrl().replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${base}${cleanPath}`, window.location.origin);

  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') {
        url.searchParams.set(k, v);
      }
    }
  }
  return url.toString();
}

async function request(method, path, { body, params } = {}) {
  const token = localStorage.getItem(TOKEN_KEY);

  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(buildUrl(path, params), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  // 204 No Content
  if (res.status === 204) return null;

  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    const err = new Error(
      (data && data.error && (typeof data.error === 'string' ? data.error : JSON.stringify(data.error))) ||
        res.statusText ||
        'Request failed'
    );
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

export const apiClient = {
  get: (path, opts) => request('GET', path, opts),
  post: (path, body) => request('POST', path, { body }),
  patch: (path, body) => request('PATCH', path, { body }),
  delete: (path) => request('DELETE', path),

  getToken: () => localStorage.getItem(TOKEN_KEY),
  setToken: (token) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  },
};
